import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { startRegisterUserRolesList } from '../../app/actions/userRoleActions';
import { startRegisterUserRoleModulesList } from '../../app/actions/userRoleModuleActions';
import { debugLogPages } from '../../app/helpers/debugHelper';
import { isFlowEngineGloballyEnabled } from '../../app/helpers/flowEngineConfigHelper';
import {
  buildModuleCheckState,
  collectModulesToSave,
  FLOW_ENGINE_ONLY_ROLE_NAMES,
  getRoleDisplayLabel,
  isSpecialistModuleRoleName,
  sortRolesByDisplayOrder
} from '../../app/helpers/specialistRoleModuleHelper';
import { RolesEnum } from '../../app/types/RolesEnum';
import CheckListComponentModules from './CheckListComponentModules';
import SpecialistRoleModulesPanel from './SpecialistRoleModulesPanel';

const CheckListContext = createContext(null);

const useCheckListContext = () => {
  const context = useContext(CheckListContext);
  if (!context) {
    throw new Error('CheckList compound components must be used within CheckListComponent.Provider');
  }
  return context;
};

const resolveRoleFlag = (item, roleFlags) => {
  const map = {
    [RolesEnum.ADMINISTRATOR]: roleFlags.isAdministrator,
    [RolesEnum.SPECIALIST_FEEDER]: roleFlags.isSpecialistFeeder,
    [RolesEnum.SPECIALIST_PRE_MANAGER]: roleFlags.isSpecialistPreManager,
    [RolesEnum.SPECIALIST_MANAGER]: roleFlags.isSpecialistManager,
    [RolesEnum.SPECIALIST_VALIDATOR]: roleFlags.isSpecialistValidator,
    [RolesEnum.SPECIALIST_VERIFICATOR]: roleFlags.isSpecialistValidator,
    [RolesEnum.SPECIALIST_EDITOR]: roleFlags.isSpecialistEditor,
    [RolesEnum.SPECIALIST_LIAISON]: roleFlags.isSpecialistLiaison,
    [RolesEnum.SPECIALIST_COORDINATOR]: roleFlags.isSpecialistCoordinator,
    [RolesEnum.END_USER]: roleFlags.isEndUser,
    [RolesEnum.END_USER_INSTITUTIONAL]: roleFlags.isEndUserInstitutional,
    [RolesEnum.CLIENT]: roleFlags.isClient,
    [RolesEnum.REPORTER]: roleFlags.isReporter
  };

  return !!map[item.name];
};

const CheckListProvider = ({ data, children }) => {
  debugLogPages(':: CheckListComponent.Provider');
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [checkedState, setCheckedState] = useState(data);
  const [isSpecialistFeederEnabled, setIsSpecialistFeederEnabled] = useState(false);
  const [modulesByRoleId, setModulesByRoleId] = useState({});
  const loadedModulesUserIdRef = useRef(null);

  const configurationState = useSelector((state) => state.configuration);
  const flowEngineEnabled = isFlowEngineGloballyEnabled(configurationState);

  const roleFlags = useSelector((state) => state.userRole.roleSelected);
  const { catalogModules } = useSelector((state) => state.catalogModule);
  const { listUserRoleModules } = useSelector((state) => state.userRoleModule);
  const { userInUserRole } = useSelector((state) => state.user);

  const rolesToDisplay = useMemo(() => (
    checkedState.filter((role) => flowEngineEnabled || !FLOW_ENGINE_ONLY_ROLE_NAMES.has(role.name))
  ), [checkedState, flowEngineEnabled]);

  const activeSpecialistRoles = useMemo(() => (
    sortRolesByDisplayOrder(
      rolesToDisplay.filter((role) => role.value && isSpecialistModuleRoleName(role.name))
    )
  ), [rolesToDisplay]);

  const updateCheckedValuesObject = () => {
    const updatedCheckedState = checkedState.map((item) => ({
      ...item,
      value: resolveRoleFlag(item, roleFlags)
    }));

    setIsSpecialistFeederEnabled(!!roleFlags.isSpecialistFeeder);
    setCheckedState(updatedCheckedState);
  };

  useEffect(() => {
    updateCheckedValuesObject();
  }, [
    roleFlags.roles,
    roleFlags.haveRole,
    roleFlags.haveOnlyOneRole,
    roleFlags.haveMultipleRoles,
    roleFlags.isAdministrator,
    roleFlags.isSpecialistFeeder,
    roleFlags.isSpecialistPreManager,
    roleFlags.isSpecialistManager,
    roleFlags.isSpecialistValidator,
    roleFlags.isSpecialistEditor,
    roleFlags.isSpecialistCoordinator,
    roleFlags.isEndUser,
    roleFlags.isEndUserInstitutional,
    roleFlags.isClient,
    roleFlags.isReporter
  ]);

  useEffect(() => {
    if (!userInUserRole?.id) {
      loadedModulesUserIdRef.current = null;
      setModulesByRoleId({});
      return;
    }

    if (loadedModulesUserIdRef.current !== userInUserRole.id) {
      loadedModulesUserIdRef.current = userInUserRole.id;
      setModulesByRoleId({});
    }
  }, [userInUserRole?.id]);

  useEffect(() => {
    if (!flowEngineEnabled || !catalogModules?.length || !userInUserRole?.id) {
      return;
    }

    if (loadedModulesUserIdRef.current !== userInUserRole.id) {
      return;
    }

    const activeRolesFromServer = data.filter(
      (item) => resolveRoleFlag(item, roleFlags) && isSpecialistModuleRoleName(item.name)
    );

    setModulesByRoleId((previousState) => {
      const nextState = { ...previousState };
      const serverRoleIds = new Set(activeRolesFromServer.map((role) => String(role.id)));

      activeRolesFromServer.forEach((role) => {
        nextState[role.id] = buildModuleCheckState(
          catalogModules,
          listUserRoleModules,
          role.id
        );
      });

      Object.keys(previousState).forEach((roleId) => {
        if (!serverRoleIds.has(String(roleId))) {
          nextState[roleId] = previousState[roleId];
        }
      });

      return nextState;
    });
  }, [
    userInUserRole?.id,
    listUserRoleModules,
    flowEngineEnabled,
    catalogModules,
    data,
    roleFlags.isSpecialistFeeder,
    roleFlags.isSpecialistPreManager,
    roleFlags.isSpecialistManager,
    roleFlags.isSpecialistValidator,
    roleFlags.isSpecialistEditor,
    roleFlags.isSpecialistCoordinator
  ]);

  const handleOnChange = (id) => {
    const updatedCheckedState = checkedState.map((item) => (
      item.id === id ? { ...item, value: !item.value } : item
    ));

    const toggledRole = updatedCheckedState.find((item) => item.id === id);

    setCheckedState(updatedCheckedState);

    if (flowEngineEnabled && toggledRole && isSpecialistModuleRoleName(toggledRole.name)) {
      setModulesByRoleId((previousState) => {
        const nextState = { ...previousState };

        if (toggledRole.value) {
          if (!nextState[toggledRole.id]) {
            nextState[toggledRole.id] = buildModuleCheckState(
              catalogModules,
              listUserRoleModules,
              toggledRole.id
            );
          }
        } else {
          delete nextState[toggledRole.id];
        }

        return nextState;
      });
    }

    if (!flowEngineEnabled) {
      if (toggledRole?.name === RolesEnum.SPECIALIST_FEEDER) {
        setIsSpecialistFeederEnabled(!!toggledRole.value);
      }
    }
  };

  const getCheckedStateById = (id) => {
    const checkedRow = checkedState.find((item) => item.id === id);
    return checkedRow?.value;
  };

  const buildRolesPayload = () => {
    const arrayToSaveRoles = [];

    checkedState.forEach((element) => {
      if (element.value) {
        arrayToSaveRoles.push({
          ...element,
          user_id: userInUserRole.id,
          catalogrole_id: element.id,
          deleted: !element.value
        });
      }
    });

    return arrayToSaveRoles;
  };

  const handleSaveRoles = () => {
    if (Object.entries(userInUserRole).length === 0) {
      Swal.fire('Warning', 'Select and user to continue', 'warning');
      return false;
    }

    dispatch(startRegisterUserRolesList(buildRolesPayload(), userInUserRole.id));
    return true;
  };

  const handleSaveAll = () => {
    if (!handleSaveRoles()) {
      return;
    }

    if (flowEngineEnabled) {
      const activeRoleIds = checkedState
        .filter((role) => role.value && isSpecialistModuleRoleName(role.name))
        .map((role) => role.id);

      const modulesForActiveRoles = {};
      activeRoleIds.forEach((roleId) => {
        if (modulesByRoleId[roleId]) {
          modulesForActiveRoles[roleId] = modulesByRoleId[roleId];
        }
      });

      const modulesPayload = collectModulesToSave(modulesForActiveRoles, userInUserRole.id);
      if (modulesPayload.length > 0 || listUserRoleModules.length > 0) {
        dispatch(startRegisterUserRoleModulesList(modulesPayload, userInUserRole.id));
      }
    }

    Swal.fire('success', 'User Roles save succesfull', 'success');
  };

  const handleToggleModule = (roleId, moduleId) => {
    setModulesByRoleId((previousState) => ({
      ...previousState,
      [roleId]: (previousState[roleId] || []).map((moduleItem) => (
        moduleItem.id === moduleId ? { ...moduleItem, value: !moduleItem.value } : moduleItem
      ))
    }));
  };

  const handleSelectAllModules = (roleId) => {
    setModulesByRoleId((previousState) => ({
      ...previousState,
      [roleId]: (previousState[roleId] || []).map((moduleItem) => ({ ...moduleItem, value: true }))
    }));
  };

  const handleDeselectAllModules = (roleId) => {
    setModulesByRoleId((previousState) => ({
      ...previousState,
      [roleId]: (previousState[roleId] || []).map((moduleItem) => ({ ...moduleItem, value: false }))
    }));
  };

  const addColumnValueToArray = (listCatalogModules) => (
    listCatalogModules.map((element) => ({
      ...element,
      value: false
    }))
  );

  const contextValue = {
    t,
    flowEngineEnabled,
    rolesToDisplay,
    activeSpecialistRoles,
    isSpecialistFeederEnabled,
    modulesByRoleId,
    catalogModules,
    handleOnChange,
    getCheckedStateById,
    handleSaveRoles,
    handleSaveAll,
    handleToggleModule,
    handleSelectAllModules,
    handleDeselectAllModules,
    addColumnValueToArray
  };

  return (
    <CheckListContext.Provider value={contextValue}>
      {children}
    </CheckListContext.Provider>
  );
};

const RolesList = () => {
  const {
    t,
    flowEngineEnabled,
    rolesToDisplay,
    activeSpecialistRoles,
    isSpecialistFeederEnabled,
    catalogModules,
    handleOnChange,
    getCheckedStateById,
    handleSaveRoles,
    handleSaveAll,
    addColumnValueToArray
  } = useCheckListContext();

  const handleClickSaveRoles = (event) => {
    event.preventDefault();

    if (flowEngineEnabled) {
      handleSaveAll();
      return;
    }

    if (handleSaveRoles()) {
      Swal.fire('success', 'User Roles save succesfull', 'success');
    }
  };

  return (
    <div>
      <hr className="line-hr" />

      <ul className="list-group">
        {rolesToDisplay.map(({ name, id }) => (
          <li key={id} className="list-group-item">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`custom-checkbox-${id}`}
                name={name}
                value={name}
                checked={getCheckedStateById(id)}
                onChange={() => handleOnChange(id)}
              />
              <label className="form-check-label" htmlFor={`custom-checkbox-${id}`}>
                {getRoleDisplayLabel(name, t)}
              </label>
            </div>
          </li>
        ))}
      </ul>

      <hr className="line-hr" />

      {!flowEngineEnabled && isSpecialistFeederEnabled && (
        <div>
          <label className="form-label" style={{ fontWeight: 'bold' }}>
            {t('admin.user_modules.module_list')}
          </label>
          <div className="control">
            {catalogModules?.length > 0 && (
              <CheckListComponentModules
                data={addColumnValueToArray(catalogModules)}
                handleSaveRolesFromModules={handleSaveRoles}
                catalogroleId={2}
              />
            )}
          </div>
        </div>
      )}

      {!flowEngineEnabled && !isSpecialistFeederEnabled && (
        <div className="d-flex justify-content-end">
          <button type="button" onClick={handleClickSaveRoles} className="btn btn-primary">
            {t('buttons.submit')}
          </button>
        </div>
      )}

      {flowEngineEnabled && activeSpecialistRoles.length === 0 && (
        <div className="d-flex justify-content-end">
          <button type="button" onClick={handleClickSaveRoles} className="btn btn-primary">
            {t('buttons.submit')}
          </button>
        </div>
      )}
    </div>
  );
};

const ModulePanelsRow = () => {
  const {
    t,
    flowEngineEnabled,
    activeSpecialistRoles,
    modulesByRoleId,
    handleSaveAll,
    handleToggleModule,
    handleSelectAllModules,
    handleDeselectAllModules
  } = useCheckListContext();

  if (!flowEngineEnabled || activeSpecialistRoles.length === 0) {
    return null;
  }

  return (
    <div className="row mt-3">
      <div className="col-12">
        <label className="form-label" style={{ fontWeight: 'bold' }}>
          {t('admin.user_roles.specialist_modules_title')}
        </label>
        <div className="d-flex flex-wrap gap-3 align-items-stretch">
          {activeSpecialistRoles.map((role) => (
            <SpecialistRoleModulesPanel
              key={role.id}
              roleName={role.name}
              modules={modulesByRoleId[role.id] || []}
              onToggleModule={(moduleId) => handleToggleModule(role.id, moduleId)}
              onSelectAll={() => handleSelectAllModules(role.id)}
              onDeselectAll={() => handleDeselectAllModules(role.id)}
            />
          ))}
        </div>
        <div className="d-flex justify-content-end mt-3">
          <button type="button" onClick={handleSaveAll} className="btn btn-primary">
            {t('buttons.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

const CheckListComponent = {
  Provider: CheckListProvider,
  RolesList,
  ModulePanelsRow
};

export default CheckListComponent;
